import {
  DateAndTimeConfigForReceipt,
  DISCOUNT_TYPE,
  RECEIPT_TYPE,
  TCreateReceiptBody,
} from "@elixir/types";
import { ReceiptStore, TStorePaymode } from "../stores/receipt-store/types";
import { Cost, DiscountType, LocalServiceEntity } from "../types/receipt";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { roundToTwoDecimals } from "@elixir/utils";

dayjs.extend(customParseFormat);
dayjs.extend(utc);
declare global {
  interface Window {
    XLSX: {
      utils: {
        json_to_sheet: Function;
        book_new: Function;
        book_append_sheet: Function;
      };
      writeFile: Function;
    };
  }
}

export const LOCAL_ID_PREFIX = "locale-";

export const DISCOUNT_TYPE_CALC: {
  [k in DISCOUNT_TYPE]: (price: number, discount: number, qt: number) => number;
} = {
  AMOUNT: (price: number, discount: number, qt: number) =>
    price * (qt || 0) - discount,
  PERCENTAGE: (price: number, discount: number, qt: number) =>
    (price - (discount / 100) * price) * (qt || 0),
};

export const totalStuff = ({
  services,
  discountOnTotal,
  paymodes,
}: {
  services: LocalServiceEntity[];
  discountOnTotal: Pick<
    Cost & {
      discountType: DiscountType;
      discountToShowWithType: string;
    },
    "discountType" | "discount"
  >;
  paymodes: TStorePaymode[];
}): {
  subTotal: number;
  additionalDiscount: number;
  totalDiscount: number;
  grandTotal: number;
  amountDue: number;
  amountPaid: number;
  totalDiscountV2OnTotalPrice: number;
} => {
  let subTotal = 0,
    totalDiscount = 0;

  services.forEach((s) => {
    if (s.cost.price) {
      subTotal = subTotal + s.cost.price * (s.cost.qty || 0);
      if (s.cost.discount) {
        totalDiscount =
          totalDiscount +
          (s.cost.price * (s.cost.qty || 0) -
            DISCOUNT_TYPE_CALC[s.cost.discountType.id](
              s.cost.price,
              s.cost.discount,
              s.cost.qty || 0
            ));
      }
    }
  });

  let grandTotal = subTotal - totalDiscount;

  const additionalDiscount =
    grandTotal -
    DISCOUNT_TYPE_CALC[discountOnTotal.discountType.id](
      grandTotal,
      discountOnTotal.discount || 0,
      1
    );

  grandTotal = subTotal - totalDiscount - additionalDiscount;

  const paymodeSum = paymodes.reduce((acc, curr) => {
    return (curr.amount ?? 0) + acc;
  }, 0);

  const amountDue = grandTotal - paymodeSum;
  const totalDiscountOfAllServices = isNaN(totalDiscount) ? 0 : totalDiscount;
  const anyAdditionalDiscount = isNaN(additionalDiscount)
    ? 0
    : additionalDiscount;
  return {
    subTotal: isNaN(subTotal) ? 0 : roundToTwoDecimals(subTotal),
    additionalDiscount: roundToTwoDecimals(anyAdditionalDiscount),
    totalDiscount: roundToTwoDecimals(totalDiscountOfAllServices),
    grandTotal: isNaN(grandTotal) ? 0 : grandTotal,
    amountDue: roundToTwoDecimals(amountDue),
    amountPaid: roundToTwoDecimals(paymodeSum),
    totalDiscountV2OnTotalPrice: roundToTwoDecimals(
      totalDiscountOfAllServices + anyAdditionalDiscount
    ),
  };
};

export function createServiceIdFromName(name: string): string {
  return `${LOCAL_ID_PREFIX}${encodeURI(name.toLowerCase())}`;
}

export const onlyPositiveNumbers = (value: string) => {
  return value.replace(/[^0-9]/g, "").replace(/(\..*)\./g, "$1");
};

export enum RECEIPT_PAYMENT_MODE {
  "UPI" = "UPI",
  "CASH" = "CASH",
  "DEBIT_CARD" = "DEBIT_CARD",
  "GPAY" = "GPAY",
  "PAYTM" = "PAYTM",
  "CREDIT_CARD" = "CREDIT_CARD",
  "EKA_WALLET" = "EKA_WALLET",
  "EKA_PREPAID" = "EKA_PREPAID",
  "OTHER" = "OTHER",
}

export const mapReceiptStoreToPayload = (
  data: ReceiptStore,
  isPaymentLink: boolean = false
): TCreateReceiptBody => {
  const {
    additionalDiscount,
    grandTotal,
    subTotal,
    totalDiscount,
    amountDue,
    amountPaid,
  } = totalStuff({
    services: data.services,
    discountOnTotal: data.discountOnTotal,
    paymodes: data.payModes,
  });

  const receiptType: RECEIPT_TYPE =
    data.receiptState === "settle-due"
      ? RECEIPT_TYPE.DUE_PAYMENT
      : amountPaid >= 0
        ? RECEIPT_TYPE.INCLINIC
        : RECEIPT_TYPE.REFUND;

  return {
    payload: {
      amount_due: receiptType === RECEIPT_TYPE.DUE_PAYMENT ? 0 : amountDue || 0,
      remarks: data?.receipt?.remarks || "",
      additional_discount_amount: Number((additionalDiscount || 0).toFixed(2)),
      additional_discount_type: data.discountOnTotal.discountType.id,
      additional_discount_value: Number(
        (data.discountOnTotal.discount || 0).toFixed(2)
      ),
      name: data.patientName,
      mobile: massageMobileNumberForReceipt(
        data?.fullPhoneNumber || data?.phoneNumber
      ),
      net_amount: Number(amountPaid.toFixed(2)),
      receipt_amount: Number(
        data.services
          .reduce((acc, curr) => {
            const na = DISCOUNT_TYPE_CALC[curr.cost.discountType.id](
              curr.cost.price,
              curr.cost.discount || 0,
              curr.cost.qty || 0
            );
            return acc + na;
          }, 0)
          .toFixed(2)
      ),
      receipt_payments: data.payModes.map((pm) => ({
        amount: Number(pm.amount.toFixed(2)),
        paymode: pm.paymode,
      })),
      receipt_skus: data.services.map((service) => {
        const net_amount = DISCOUNT_TYPE_CALC[service.cost.discountType.id](
          service.cost.price,
          service.cost.discount || 0,
          service.cost.qty || 0
        );
        return {
          service_id: service.id || null,
          service_name: service.name.toLowerCase(),
          quantity: service.cost.qty || 0,
          price: Number(service.cost.price.toFixed(2)),
          amount: Number(
            (service.cost.price * (service.cost.qty || 0)).toFixed(2)
          ),
          discount_value: Number((service.cost.discount || 0).toFixed(2)),
          discount_amount: Number(
            (service.cost.price * (service.cost.qty || 0) - net_amount).toFixed(
              2
            )
          ),
          discount_type: service.cost.discountType.id,
          net_amount: Number(net_amount.toFixed(2)),
        };
      }),
    },
    meta: {
      clinic_id: data.clinicId!,
      patient_oid: data.patientId!,
      aid: data?.aid,
      ...(isPaymentLink && { request_payment: true }),
      receipt_type: receiptType,
    },
  };
};
export const massageMobileNumberForReceipt = (
  phoneNum: string = ""
): string => {
  if (!phoneNum) return "";

  const cleanedNumber = phoneNum.trim();
  if (cleanedNumber.length <= 3) return "";

  let finalNum = "";
  if (cleanedNumber.startsWith("+") && cleanedNumber.length > 10) {
    finalNum = cleanedNumber;
  } else if (cleanedNumber.length === 10) {
    finalNum = `+91${cleanedNumber}`;
  } else {
    finalNum = "";
  }
  return finalNum;
};

export const mapReceiptStoreToPayloadForSettleDue = (
  data: ReceiptStore,
  isPaymentLink: boolean = false
): TCreateReceiptBody => {
  return {
    payload: {
      amount_due: 0,
      remarks: data?.receipt?.remarks || "",
      additional_discount_amount: 0,
      additional_discount_type: data.discountOnTotal.discountType.id,
      additional_discount_value: Number(
        (data.discountOnTotal.discount || 0).toFixed(2)
      ),
      name: data.patientName,
      mobile: massageMobileNumberForReceipt(
        data?.fullPhoneNumber || data?.phoneNumber
      ),
      net_amount: data.amountSettled || 0,
      receipt_amount: data.amountSettled || 0,
      receipt_payments: data.payModes.map((pm) => ({
        amount: Number(pm.amount.toFixed(2)),
        paymode: pm.paymode,
      })),
      receipt_skus: [],
    },
    meta: {
      clinic_id: data.clinicId!,
      patient_oid: data.patientId!,
      aid: data?.aid,
      receipt_type: RECEIPT_TYPE.DUE_PAYMENT,
    },
  };
};

export const finalVisitDateRenderer = (
  dateAndTime: DateAndTimeConfigForReceipt | undefined,
  inputDate: string | number | undefined
): string => {
  if (!inputDate) return "";

  let d: dayjs.Dayjs;

  if (typeof inputDate === "number") {
    d = dayjs(inputDate);
  } else if (typeof inputDate === "string") {
    if (inputDate.includes("T")) {
      d = dayjs(inputDate);
    } else {
      d = dayjs(inputDate, "YYYY-MM-DD HH:mm:ss.SSS", true);
    }
  } else {
    return "";
  }

  if (!d.isValid()) return "";

  switch (dateAndTime) {
    case DateAndTimeConfigForReceipt.DATE_AND_TIME:
      return d.format("Do MMMM, YYYY, h:mm A");

    case DateAndTimeConfigForReceipt.EXTENDED_DATE_AND_TIME:
      return d.format("dddd, MMMM D, YYYY h:mm A");

    case DateAndTimeConfigForReceipt.DATE_ONLY:
    default:
      return d.format("Do MMMM, YYYY");
  }
};

export const safeReturnOfNumber = (inputNum: string | number | null) => {
  let finalNum = 0;
  if (typeof inputNum === "number") finalNum = inputNum;
  if (!inputNum || isNaN(finalNum)) finalNum = 0;

  return finalNum;
};
